import { NoteInternalAssessment } from '../actions';

type TreatmentApproach = { id: string; name: string; order: number };
type Intervention = {
  id: string;
  treatmentApproachId: string;
  name: string;
};
type TreatmentApproachSelection = { approach: TreatmentApproach; interventions: Intervention[] };

export type SessionFormFieldDependency = {
  fieldId: string;
  value: boolean | string | number | string[];
  condition?: 'eq' | 'not_eq' | 'in' | 'not_in';
  behavior: 'hide' | 'disable';
};

type Adornment = {
  location: 'start' | 'end';
  value: string;
};

type ServerFormFieldValidatorOneOf = {
  type: 'one_of';
  values: (string | number | boolean)[];
  errorMessage: string;
};

export type ServerFormFieldValidator = ServerFormFieldValidatorOneOf; // can be added more validators later

type _SessionFormField<T extends Record<string, unknown>> = {
  id: string;
  label: string;
  optional: boolean;
  helperText: string | null;
  dependency?: SessionFormFieldDependency;
  toggleVisibility?: {
    visibleOnValue: boolean;
    label: string;
  };
  emptyFieldErrorMessage: string;
  layout: {
    topSpacing: boolean;
    bottomSpacing: boolean;
    fullWidth: boolean;
    whiteBackground: boolean;
    leftSpacing: boolean;
  };

  value?: unknown;
  defaultValue?: unknown;

  validators?: ServerFormFieldValidator[];
} & T;

export type SessionFormFieldsGroup = {
  id: string;
  label?: string;
  fieldType: 'fields_group';
  formFields: (SessionFormField | SessionFormFieldsGroup)[];
  dependency?: SessionFormFieldDependency;
};

type StaticDividerField = _SessionFormField<{
  fieldType: 'divider';
  value: null;
  defaultValue: null;
}>;

type StaticLinkField = _SessionFormField<{
  fieldType: 'static_link';
  href: string;
  value: null;
  defaultValue: null;
}>;

type StaticLabelField = _SessionFormField<{
  fieldType: 'static_label';
  value: null;
  defaultValue: null;
}>;

type StaticHelperTextField = _SessionFormField<{
  fieldType: 'static_helper_text';
  value: null;
  defaultValue: null;
}>;

type NumberField = _SessionFormField<{
  fieldType: 'integer';
  renderAs?: 'weight' | 'height' | 'adorned';
  value: number;
  defaultValue?: number;
  adornment?: Adornment;
}>;

type DiagnosisField = _SessionFormField<{
  fieldType: 'diagnosis';
  value: string[];
  defaultValue?: string[];
}>;

type YesOrOtherField = _SessionFormField<{
  fieldType: 'yes_or_other';
  yesValue: string;
  noValue: string;
  value?: string;
  defaultValue?: string;
}>;

type DateField = _SessionFormField<{
  fieldType: 'date';
  value: string | null;
  defaultValue: null;
  dateFormat: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'MM/YYYY';
  renderAs: 'picker_only' | 'keyboard_only' | 'inline_button';
  minDate?: string;
  maxDate?: string;
}>;

type SelectOption = {
  value: string;
  label?: string;
  disabled?: boolean;
};

type SelectStringField = _SessionFormField<{
  fieldType: 'select_string';
  options: (string | SelectOption)[];
  value: string | null;
  defaultValue: null;

  renderAs: 'radio' | 'inline_radio' | 'checkbox' | 'dropdown' | 'adorned';
  noSelectionOptionText: null;
  valueDisplayText: Record<string, string>;
  valueDisplayTextAsLabel: boolean;
}>;

type SelectMultipleStringField = _SessionFormField<{
  fieldType: 'select_multiple_string';
  options: string[];
  value: string[];
  defaultValue: string[];
  minimumValues?: number;

  renderAs: 'radio' | 'checkbox' | 'dropdown';
  noSelectionOptionText: string | null;
  allowCustomOption: boolean;
  exclusiveOn?: string;
}>;

type BooleanField = _SessionFormField<{
  fieldType: 'boolean';
  options?: null;
  value?: boolean | null;
  defaultValue?: null;

  renderAs: 'radio' | 'yesNo' | 'checkbox' | 'toggleButton';
  noSelectionOptionText?: null;
  allowCustomOption?: null;
}>;

type TextAreaField = _SessionFormField<{
  fieldType: 'text_area';
  options: null;
  placeholder?: string;
  value: string | null;
  defaultValue: null;
  minRows: number;
  minLength?: number;

  renderAs: null;
  noSelectionOptionText: null;
  allowCustomOption: null;
}>;

type TextField = _SessionFormField<{
  fieldType: 'text_field';
  options: null;
  placeholder?: string;
  value: string | null;
  defaultValue: null;

  renderAs: null;
  noSelectionOptionText: null;
  allowCustomOption: null;
}>;

type EmailField = _SessionFormField<{
  fieldType: 'email';
  value: string | null;
}>;

type PhoneNumberField = _SessionFormField<{
  fieldType: 'phone_number';
  value: string | null;
}>;

type ComboBoxField = _SessionFormField<{
  fieldType: 'combo_box';
  valueType: 'string' | 'integer' | 'float';
  value: string | null;
  options: string[];

  allowCustomOption?: boolean;
  adornment?: Adornment;
  applyAdornmentToOptions?: boolean;
}>;

export type SessionFormField = (
    | SelectStringField
    | SelectMultipleStringField
    | BooleanField
    | TextAreaField
    | TextField
    | DateField
    | NumberField
    | DiagnosisField
    | YesOrOtherField
    | StaticLinkField
    | StaticLabelField
    | StaticHelperTextField
    | StaticDividerField
    | EmailField
    | PhoneNumberField
    | ComboBoxField
);

export type SessionForm = {
  formFields: SessionFormField[];
};

export type SessionFormFieldError = {
  id: string;
  message: string;
};

export type SessionNotes = {
  sessionId: string;

  isDiagnosisReviewed?: boolean;
  isDiagnosisReviewRequired?: boolean;

  // progress form to be selected depends on isInitialSession value
  isInitialSession: boolean;
  riskAssessmentOpted: boolean;
  mentalStatusExamOpted: boolean;
  initialProgress: SessionForm;
  followUpProgress: SessionForm;

  isFollowUpTreatmentGoalReviewed?: boolean;
  isFollowUpTreatmentGoalReviewRequired?: boolean;

  assessments: NoteInternalAssessment[];

  mentalStatusExam: SessionForm;
  riskAssessment: SessionForm;
  presentingProblems: SessionForm;
  treatment: {
    approachSelections: TreatmentApproachSelection[];
    isTreatmentReviewed?: boolean;
    isTreatmentReviewRequired?: boolean;
    homework?: string | null;
  };
  additionalDetails?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  createdAt: string;
  updatedAt: string;
};


export type SessionNotesView = {
  notes: SessionNotes;
  mentalStatusExamNote: string;
  presentingProblems: {
    problems: string;
  };
  riskAssessmentNote: {
    summary: string;
    riskFactors: string;
    protectiveFactors: string;
    additionalDetails: string | null;
  };
  treatmentApproachNote: string[];
  initialProgressNote: {
    goal: string;
    objective: string;
    treatmentFrequency: string;
    referrals: string;
  };
  followUpProgressNote: {
    treatmentGoals: string | null;
    barrierToGoals: string | null;
    recommendation: string;
    referrals: string;
  };
};

export type Addendum = {
  id: string;
  content: string;
  createdAt: string;
  submittedBillingUpdate: boolean;
};


export type SessionServiceType = 'audio' | 'audio_and_video' | 'in_person';
export type SessionParticipation = 'client_only' | 'client_and_family_member' | 'family_member';

export type Session = {
  appointmentId: string;
  clinicalNote: string;
  cptCodeId: string;
  addonCptCodeIds: string[];
  diagnosisIds: string[];
  createdAt: string;
  id: string;
  serviceType: SessionServiceType;
  participation: SessionParticipation;
  insurance_company_id?: string;
  locationId?: string;
  sessionNote?: string;
  submittedAt?: string;
  updatedAt: string;
};


export type SessionResource = {
  session: Session;
  notes: SessionNotes;
  addenda: Addendum[];
  followUpProgressErrors: SessionFormFieldError[];
  initialProgressErrors: SessionFormFieldError[];
  presentingProblemsErrors: SessionFormFieldError[];
  mseErrors: SessionFormFieldError[];
  riskAssessmentErrors: SessionFormFieldError[];
};


export const sessionServiceOptionsDict: { [key in SessionServiceType]: string } = {
  audio_and_video: 'Virtual (Audio + Video)',
  audio: 'Virtual (Audio only)',
  in_person: 'In-person',
};

export const sessionParticipantOptionsDict: { [key in SessionParticipation]: string } = {
  client_only: 'Client only',
  client_and_family_member: 'Client + Family member',
  family_member: 'Family member (Client not present)'
};
